import { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, API } from "@/data/site";
import { FadeUp, Chapter, RevealLines } from "./motion";

const INFO = [
  { icon: Phone, label: "Call Us", value: `${CONTACT.phone} · ${CONTACT.phone2}`, href: `tel:${CONTACT.phoneRaw}`, testid: "contact-phone" },
  { icon: MessageCircle, label: "WhatsApp", value: "Chat instantly", href: `https://wa.me/${CONTACT.whatsapp}`, testid: "contact-whatsapp" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
  { icon: MapPin, label: "Studio", value: CONTACT.address, testid: "contact-address" },
  { icon: Clock, label: "Hours", value: CONTACT.hours, testid: "contact-hours" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "contact_form" }),
      });
      if (!res.ok) throw new Error();
      toast.success("Thank you! Sohan's team will call you within 2 hours.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong — please WhatsApp us instead");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "h-12 w-full border-b border-[#1B1B1B]/20 bg-transparent text-sm text-[#1B1B1B] placeholder:text-[#2C2C2C]/40 outline-none transition-colors duration-300 focus:border-[#B68D40]";

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#F7F5F2] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Chapter num="14" title="Begin" />
        <RevealLines
          className="mt-8 font-display text-4xl tracking-tight text-[#1B1B1B] md:text-6xl"
          lines={[<>Your home is</>, <><span className="italic text-[#B68D40]">waiting.</span></>]}
        />

        <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <div className="space-y-1">
              {INFO.map((item, i) => (
                <FadeUp key={item.label} delay={i * 0.07}>
                  <div className="flex items-center gap-5 border-b hairline py-5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border hairline-gold text-[#B68D40]">
                      <item.icon size={17} />
                    </span>
                    <div>
                      <p className="font-num text-[10px] uppercase tracking-[0.3em] text-[#2C2C2C]/45">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} data-testid={item.testid} className="mt-1 block text-sm font-medium text-[#1B1B1B] transition-colors hover:text-[#B68D40]">
                          {item.value}
                        </a>
                      ) : (
                        <p data-testid={item.testid} className="mt-1 text-sm font-medium text-[#1B1B1B]">{item.value}</p>
                      )}
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.35} className="mt-8 overflow-hidden rounded-xl border hairline">
              <iframe
                title="Bengaluru Interiors studio location"
                data-testid="contact-map"
                src="https://maps.google.com/maps?q=Nagarbhavi%202nd%20Stage%2C%20Bengaluru&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="h-[240px] w-full grayscale-[0.4] contrast-[1.05]"
                loading="lazy"
              />
            </FadeUp>
          </div>

          <FadeUp delay={0.15}>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border hairline bg-white p-8 shadow-[0_8px_32px_rgba(27,27,27,0.04)] md:p-12">
              <p className="font-display text-2xl text-[#1B1B1B]">Schedule your <span className="italic text-[#B68D40]">free</span> consultation</p>
              <p className="mt-2 text-xs text-[#2C2C2C]/55">Site visit · design direction · transparent quote — all free.</p>
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <input data-testid="contact-name-input" className={inputCls} placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input data-testid="contact-phone-input" className={inputCls} placeholder="Phone / WhatsApp *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <input data-testid="contact-email-input" className={inputCls} placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input data-testid="contact-message-input" className={inputCls} placeholder="Tell us about your home" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="mt-10 flex w-full items-center justify-center gap-3 rounded-full bg-[#1B1B1B] py-4 text-sm font-semibold uppercase tracking-widest text-[#F7F5F2] transition-colors duration-300 hover:bg-[#B68D40] hover:text-[#1B1B1B] disabled:opacity-60"
              >
                {sending ? "Sending…" : "Schedule Free Consultation"}
                <Send size={15} />
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
